import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useDeleteStoriesByTripByTripIdStoryAndStoryIdMutation,
  usePatchStoriesByTripByTripIdStoryAndStoryIdMutation,
  usePostStoriesByTripByTripIdMutation,
} from '../../store/backendApi';
import { FileUploadResult, useUploadFiles } from '../../utils/upload';
import { Story } from '../../schema';
import { z } from 'zod';

type AlertState = 'none' | 'uploading' | 'success' | 'error';

// Validation schema based on API requirements
const storyFormSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200, 'Title must be less than 200 characters'),
  description: z
    .string()
    .max(2000, 'Description must be less than 2000 characters')
    .optional()
    .or(z.literal('')),
  date: z.string().min(1, 'Date and time are required'),
  place: z.string().max(50, 'Place must be less than 50 characters').optional().or(z.literal('')),
});

type ValidationErrors = {
  title?: string;
  description?: string;
  date?: string;
  place?: string;
};

interface UseEditStoryFormReturn {
  // Form state
  storyDateTime: string | null;
  storyPlace: string | undefined;
  storyTitle: string;
  storyDescription: string | undefined;

  // Form handlers
  setStoryDateTime: (dateTime: string | null) => void;
  setStoryPlace: (place: string) => void;
  setStoryTitle: (title: string) => void;
  setStoryDescription: (desc: string) => void;

  // Media management
  uploadedMedia: FileUploadResult[];
  uploadingMediaCount: number | null;
  handleMediaUpload: (files: File[]) => void;
  reorderMedia: (oldIndex: number, newIndex: number) => void;
  deleteMedia: (mediaId: string) => void;

  // Confirmation states
  confirmingDelete: string | null;
  setConfirmingDelete: (id: string | null) => void;
  confirmingDeleteStory: boolean;
  setConfirmingDeleteStory: (value: boolean) => void;

  // Operations
  createStory: () => Promise<Story | null>;
  updateStory: () => Promise<void>;
  deleteStory: () => Promise<void>;

  // Status
  hasFormChanges: boolean;
  isCreatingStory: boolean;
  patchStoryLoading: boolean;
  deleteStoryLoading: boolean;

  // Alert state
  alertState: AlertState;
  uploadingCount: number;

  // Add photo tile style (computed)
  addPhotoTileStyle: { height: string; width: string };

  // Validation
  validationErrors: ValidationErrors;
  validateField: (field: keyof ValidationErrors) => void;
  validateAllFields: () => boolean;
  isFormValid: boolean;
}

/**
 * Hook to manage story editing form with complex state and media uploads.
 * Platform-agnostic: Media operations work everywhere, drag-drop is pure array manipulation.
 *
 * Handles:
 * - Story form fields (title, description, place, date)
 * - Multiple media uploads with progress tracking
 * - Media reordering (DnD is just array manipulation)
 * - Media deletion with confirmation
 * - Story creation and update operations
 * - Change detection
 * - Alert/notification states
 *
 * Mode is derived from story parameter:
 * - story === null: Create mode (initial story creation, no photos yet)
 * - story !== null: Edit mode (full editing with photos)
 *
 * @param story - The story to edit (null in create mode)
 * @param tripId - The trip ID for creating new stories
 * @param onClose - Callback when modal should close
 * @returns Story form state, handlers, and operations
 */
export const useEditStoryForm = (
  story: Story | null,
  tripId: string | undefined,
  onClose: () => void,
): UseEditStoryFormReturn => {
  const { uploadFiles } = useUploadFiles();

  // Form state
  // Initialize with story data if available, otherwise use defaults
  // The useEffect below will sync when story/mode changes
  const [storyDateTime, setStoryDateTime] = useState<string | null>(() => {
    // If story exists with date, use it (edit mode)
    if (story?.date) {
      // If the date already includes time, use it as-is
      if (story.date.includes('T')) {
        return story.date;
      }
      // If it's just a date, add noon as default time (timezone-agnostic)
      return `${story.date}T12:00:00`;
    }
    // No story or no date - default to current date/time (create mode)
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  });
  const [storyPlace, setStoryPlace] = useState<string | undefined>(story?.place);
  const [storyTitle, setStoryTitle] = useState<string>(story?.title || '');
  const [storyDescription, setStoryDescription] = useState<string | undefined>(story?.description);

  // API mutations
  const [createStoryMutation, { isLoading: isCreatingStory }] =
    usePostStoriesByTripByTripIdMutation();
  const [patchStory, { isLoading: patchStoryLoading }] =
    usePatchStoriesByTripByTripIdStoryAndStoryIdMutation();
  const [callDeleteStory, { isLoading: deleteStoryLoading }] =
    useDeleteStoriesByTripByTripIdStoryAndStoryIdMutation();

  // Media state
  const [uploadedMedia, setUploadedMedia] = useState<FileUploadResult[]>(
    story?.medias.map((u) => ({
      success: true,
      fileUpload: {
        uploadUrl: new URL(`https://placeholder/${u.id}.${u.ext}`),
        uploadId: u.id,
        localFile: undefined,
        dimensions: u.dimensions,
      },
    })) || [],
  );
  const [uploadingMediaCount, setUploadingMediaCount] = useState<number | null>(null);
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);

  // Alert/notification state
  const [failedEvent, setFailedEvent] = useState(false);

  // Confirmation states
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);
  const [confirmingDeleteStory, setConfirmingDeleteStory] = useState(false);

  // Validation state
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [touchedFields, setTouchedFields] = useState<Set<keyof ValidationErrors>>(new Set());

  // Reset form state when story changes (handles switching from create to edit mode)
  useEffect(() => {
    if (story) {
      // Reset to story values (edit mode)
      const normalizedDateTime = (() => {
        if (!story.date) return null;
        if (story.date.includes('T')) return story.date;
        return `${story.date}T12:00:00`;
      })();

      setStoryDateTime(normalizedDateTime);
      setStoryPlace(story.place);
      setStoryTitle(story.title || '');
      setStoryDescription(story.description);
      setUploadedMedia(
        story.medias.map((u) => ({
          success: true,
          fileUpload: {
            uploadUrl: new URL(`https://placeholder/${u.id}.${u.ext}`),
            uploadId: u.id,
            localFile: undefined,
            dimensions: u.dimensions,
          },
        })) || [],
      );
      // Clear validation errors when switching stories
      setValidationErrors({});
      setTouchedFields(new Set());
    } else {
      // Reset to defaults (create mode)
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');

      setStoryDateTime(`${year}-${month}-${day}T${hours}:${minutes}:${seconds}`);
      setStoryPlace(undefined);
      setStoryTitle('');
      setStoryDescription(undefined);
      setUploadedMedia([]);
      setValidationErrors({});
      setTouchedFields(new Set());
    }
  }, [story]);

  // Validation function
  const validateField = useCallback(
    (field: keyof ValidationErrors) => {
      const formData = {
        title: storyTitle,
        description: storyDescription || '',
        date: storyDateTime || '',
        place: storyPlace || '',
      };

      try {
        // Validate individual field
        const fieldValue = formData[field];
        if (field === 'title') {
          storyFormSchema.shape.title.parse(fieldValue);
        } else if (field === 'description') {
          storyFormSchema.shape.description.parse(fieldValue);
        } else if (field === 'date') {
          storyFormSchema.shape.date.parse(fieldValue);
        } else if (field === 'place') {
          storyFormSchema.shape.place.parse(fieldValue);
        }

        setValidationErrors((prev) => {
          const updated = { ...prev };
          delete updated[field];
          return updated;
        });
      } catch (error) {
        if (error instanceof z.ZodError) {
          const fieldError = error.errors[0];
          setValidationErrors((prev) => ({
            ...prev,
            [field]: fieldError.message,
          }));
        }
      }
      setTouchedFields((prev) => new Set(prev).add(field));
    },
    [storyTitle, storyDescription, storyDateTime, storyPlace],
  );

  // Computed: Form validity
  const isFormValid = useMemo(() => {
    const formData = {
      title: storyTitle,
      description: storyDescription || '',
      date: storyDateTime || '',
      place: storyPlace || '',
    };

    try {
      storyFormSchema.parse(formData);
      return true;
    } catch {
      return false;
    }
  }, [storyTitle, storyDescription, storyDateTime, storyPlace]);

  // Computed: Add photo tile style based on first photo dimensions
  const addPhotoTileStyle = useMemo(() => {
    const firstPhoto = uploadedMedia[0]?.fileUpload?.dimensions;
    if (firstPhoto) {
      const maxHeight = 200;
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
      const maxWidthPx =
        (isMobile ? 28 : 40 / 100) * (typeof window !== 'undefined' ? window.innerWidth : 800);
      const width = Math.round(maxHeight * (firstPhoto.width / firstPhoto.height));
      const finalWidth = Math.min(width, maxWidthPx);
      const finalHeight =
        width > maxWidthPx
          ? Math.round(maxWidthPx * (firstPhoto.height / firstPhoto.width))
          : maxHeight;
      return { height: `${finalHeight}px`, width: `${finalWidth}px` };
    } else {
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
      const defaultSize = isMobile ? '150px' : '200px';
      return { height: defaultSize, width: defaultSize };
    }
  }, [uploadedMedia]);

  // Computed: Has form changes
  const hasFormChanges = useMemo(() => {
    if (!story) return false;

    const titleChanged = storyTitle !== story.title;
    const descriptionChanged = storyDescription !== story.description;
    const placeChanged = storyPlace !== story.place;

    // Normalize both dates for comparison (handle both date-only and datetime formats)
    const normalizeDate = (date: string | null | undefined): string | null => {
      if (!date) return null;
      return date.includes('T') ? date : `${date}T12:00:00`;
    };

    const originalDateTime = normalizeDate(story.date);
    const currentDateTime = storyDateTime;
    const dateTimeChanged = originalDateTime !== currentDateTime;

    const originalMediaIds = story.medias.map((m) => m.id);
    const currentMediaIds = uploadedMedia
      .filter((m) => m.fileUpload)
      .map((m) => m.fileUpload?.uploadId)
      .filter((id): id is string => !!id);
    const mediaChanged = JSON.stringify(originalMediaIds) !== JSON.stringify(currentMediaIds);

    return titleChanged || descriptionChanged || placeChanged || dateTimeChanged || mediaChanged;
  }, [storyTitle, storyDescription, storyPlace, storyDateTime, uploadedMedia, story]);

  // Computed: Alert state
  const alertState: AlertState = useMemo(() => {
    if (failedEvent) return 'error';
    if (uploadingMediaCount && uploadingMediaCount > 0) return 'uploading';
    if (uploadingMediaCount === 0) return 'success';
    return 'none';
  }, [failedEvent, uploadingMediaCount]);

  // Operations
  const showFailure = useCallback(() => {
    setFailedEvent(true);
    setTimeout(() => {
      setFailedEvent(false);
    }, 2000);
  }, []);

  const onFileUploaded = useCallback(
    async (fileUploadResult: FileUploadResult) => {
      if (!fileUploadResult.success) {
        showFailure();
        // Remove failed placeholder
        if (fileUploadResult.originalIndex !== undefined) {
          setUploadedMedia((prev) => {
            const newMedia = [...prev];
            // Find placeholder with matching index
            const placeholderIdx = newMedia.findIndex(
              (m) => m.fileUpload?.originalIndex === fileUploadResult.originalIndex && !m.success,
            );
            if (placeholderIdx !== -1) {
              newMedia.splice(placeholderIdx, 1);
            }
            return newMedia;
          });
        }
        setUploadingMediaCount((prev) => (prev || 0) - 1);
        return;
      }

      // Replace placeholder at correct position instead of prepending
      setUploadedMedia((prev) => {
        const newMedia = [...prev];
        const index = fileUploadResult.originalIndex ?? -1;

        if (index >= 0) {
          // Find the placeholder with matching originalIndex
          const placeholderIdx = newMedia.findIndex(
            (m) => m.fileUpload?.originalIndex === index && !m.success,
          );

          if (placeholderIdx !== -1) {
            // Replace placeholder with actual result
            newMedia[placeholderIdx] = fileUploadResult;
          } else {
            // Fallback: append if placeholder not found (shouldn't happen)
            newMedia.push(fileUploadResult);
          }
        } else {
          // Fallback: append if no index (shouldn't happen)
          newMedia.push(fileUploadResult);
        }

        return newMedia;
      });
      setUploadingMediaCount((prev) => (prev || 0) - 1);
    },
    [showFailure],
  );

  // Handle file uploads
  useEffect(() => {
    if (!story || filesToUpload.length === 0) return;

    uploadFiles({
      tripId: story.tripId,
      storyId: story.id,
      files: filesToUpload,
      onUploaded: onFileUploaded,
    }).catch(() => {
      showFailure();
    });
    setFilesToUpload([]);
  }, [filesToUpload, story, uploadFiles, onFileUploaded, showFailure]);

  const handleMediaUpload = useCallback((files: File[]) => {
    setUploadingMediaCount((prev) => (prev || 0) + files.length);

    // Create placeholder entries for each file in correct order
    const placeholders: FileUploadResult[] = files.map((file, index) => ({
      success: false, // Mark as pending
      fileUpload: {
        uploadUrl: new URL(`https://placeholder/pending-${Date.now()}-${index}`),
        uploadId: `pending-${Date.now()}-${index}`,
        localFile: file,
        dimensions: { width: 0, height: 0 }, // Unknown until upload
        originalIndex: index,
      },
    }));

    // Add placeholders to the END of array (maintain order)
    setUploadedMedia((prev) => [...prev, ...placeholders]);

    setFilesToUpload(files);
  }, []);

  const reorderMedia = useCallback((oldIndex: number, newIndex: number) => {
    setUploadedMedia((items) => {
      const result = Array.from(items);
      const [removed] = result.splice(oldIndex, 1);
      result.splice(newIndex, 0, removed);
      return result;
    });
  }, []);

  const deleteMedia = useCallback((mediaId: string) => {
    setUploadedMedia((prev) =>
      prev.filter((media) => media.fileUpload?.uploadUrl.pathname !== mediaId),
    );
    setConfirmingDelete(null);
  }, []);

  const updateStory = useCallback(async () => {
    if (!story) return;

    const uploads = uploadedMedia
      .filter((media) => media.fileUpload)
      .map((media) => {
        const ext = media.fileUpload?.uploadUrl.pathname.split('.').pop() || 'jpg';
        return {
          id: media.fileUpload?.uploadId || '',
          ext: ext,
          dimensions: media.fileUpload?.dimensions || { width: 0, height: 0 },
        };
      })
      .filter((upload) => upload.id);

    await patchStory({
      tripId: story.tripId,
      storyId: story.id,
      partialStoryBodyRequest: {
        title: storyTitle,
        description: storyDescription ?? '',
        place: storyPlace,
        // Send ISO timestamp string (without timezone - stored as-is)
        date: storyDateTime ?? undefined,
        medias: uploads,
      },
    });
    onClose();
  }, [
    story,
    uploadedMedia,
    storyTitle,
    storyDescription,
    storyPlace,
    storyDateTime,
    patchStory,
    onClose,
  ]);

  const deleteStory = useCallback(async () => {
    if (!story) return;

    await callDeleteStory({
      tripId: story.tripId,
      storyId: story.id,
    });
    onClose();
  }, [story, callDeleteStory, onClose]);

  const createStory = useCallback(async (): Promise<Story | null> => {
    if (!tripId) return null;

    try {
      const newStory = await createStoryMutation({
        tripId,
        storyBodyRequest: {
          title: storyTitle || '',
          description: storyDescription || '',
          place: storyPlace || '',
          date: storyDateTime || new Date().toISOString(),
          medias: [],
        },
      }).unwrap();
      return newStory;
    } catch (error) {
      console.error('Failed to create story:', error);
      return null;
    }
  }, [tripId, storyTitle, storyDescription, storyPlace, storyDateTime, createStoryMutation]);

  // Validate all fields at once - used when user clicks submit button
  const validateAllFields = useCallback((): boolean => {
    const formData = {
      title: storyTitle,
      description: storyDescription || '',
      date: storyDateTime || '',
      place: storyPlace || '',
    };

    try {
      storyFormSchema.parse(formData);
      // Clear all errors if validation passes
      setValidationErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors: ValidationErrors = {};
        error.errors.forEach((err) => {
          const field = err.path[0] as keyof ValidationErrors;
          errors[field] = err.message;
        });
        setValidationErrors(errors);
      }
      return false;
    }
  }, [storyTitle, storyDescription, storyDateTime, storyPlace]);

  return {
    storyDateTime,
    storyPlace,
    storyTitle,
    storyDescription,
    setStoryDateTime,
    setStoryPlace,
    setStoryTitle,
    setStoryDescription,
    uploadedMedia,
    uploadingMediaCount,
    handleMediaUpload,
    reorderMedia,
    deleteMedia,
    confirmingDelete,
    setConfirmingDelete,
    confirmingDeleteStory,
    setConfirmingDeleteStory,
    createStory,
    updateStory,
    deleteStory,
    hasFormChanges,
    isCreatingStory,
    patchStoryLoading,
    deleteStoryLoading,
    alertState,
    uploadingCount: uploadingMediaCount || 0,
    addPhotoTileStyle,
    validationErrors,
    validateField,
    validateAllFields,
    isFormValid,
  };
};
